import { makeAutoObservable, runInAction } from "mobx";
import { Company } from "types/company.type";
import { toast } from "react-toastify";
import { companyService } from "services/company.service";
import authStore from "stores/auth/AuthStore";

class CompanyStore {
  companies: Company[] = [];
  subCompanies: Company[] = [];
  loading = false;
  updatingCompanies: Map<number, boolean> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  async fetchCompanies() {
    if (!authStore.isAuthenticated()) return;

    this.loading = true;
    try {
      const data = await companyService.getAll();
      runInAction(() => {
        this.companies = data;
      });
    } catch (error: any) {
      if (
        error?.response?.data?.type === "role_changed" ||
        error?.response?.data?.type === "account_inactive"
      ) {
        return;
      }
      toast.error("Erreur lors du chargement des entreprises");
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async fetchSubCompanies(companyId: number) {
    if (!companyId) return;
    this.loading = true;
    try {
      const data = await companyService.getSubCompanies(companyId);
      runInAction(() => {
        this.subCompanies = data;
      });
    } catch (error) {
      toast.error("Erreur lors du chargement des sous-entreprises");
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async saveCompany(company: Partial<Company>) {
    try {
      const formData = new FormData();

      for (const key in company) {
        const value = (company as any)[key];
        if (value !== null && value !== undefined) {
          if (key === "subCompanies") {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value);
          }
        }
      }
      if (company?.id) {
        const updated = await companyService.update(company.id, formData);
        runInAction(() => {
          const index = this.companies.findIndex((c) => c.id === company.id);
          if (index > -1) {
            this.companies[index] = {
              ...this.companies[index],
              ...updated.data,
            };
          }
        });
        toast.success("Entreprise mise à jour !");
      } else {
        const created = await companyService.create(formData);
        runInAction(() => {
          this.companies.unshift(created.data);
        });
        toast.success("Entreprise créée !");
      }
    } catch (error) {
      // toast.error("Erreur lors de l'enregistrement");
      throw error;
    }
  }

  async deleteCompany(id: number) {
    this.loading = true;
    try {
      const result: any = await companyService.delete(id);

      runInAction(() => {
        if (
          result?.data?.status === "deleted" ||
          result?.data?.status === "archived"
        ) {
          this.companies = this.companies.filter((c) => c.id !== id);
        }
      });

      toast.success(result?.data?.message || "Entreprise supprimée !");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async updateCompanyStatus(id: number, isActive: boolean) {
    this.updatingCompanies.set(id, true);
    try {
      const formData = new FormData();
      formData.append("isActive", isActive?.toString());

      await companyService.update(id, formData);

      runInAction(() => {
        const company = this.companies.find((c) => c.id === id);
        if (company) company.isActive = isActive;
      });

      toast.success(`Entreprise ${isActive ? "activée" : "désactivée"} !`);
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du statut");
      throw error;
    } finally {
      runInAction(() => {
        this.updatingCompanies.set(id, false);
      });
    }
  }

  isCompanyUpdating(id: number) {
    return this.updatingCompanies.get(id) ?? false;
  }
}

const companyStore = new CompanyStore();
export default companyStore;
